import { useState } from 'react';

import {
  disableTotpApi,
  enableTotpApi,
  getMfaStatusApi,
  getTrustedDevicesApi,
  regenerateBackupCodesApi,
  revokeAllTrustedDevicesApi,
  revokeTrustedDeviceApi,
  setupTotpApi,
  updateSecuritySettingsApi,
} from '@/lib/auth/authApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import type { MfaStatus, TrustedDevice } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle,
  ClipboardCopy,
  Download,
  Loader2,
  RefreshCw,
  Shield,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

// ─── Helpers ──────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ttlSchema = z.object({
  trustedDeviceTtlDays: z.number().int().min(1).max(90),
});

// ─── Section Statut 2FA ────────────────────────────────────────

function MfaStatusSection({ status }: { status: MfaStatus }) {
  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-6'>
      <h2 className='mb-4 flex items-center gap-2 text-lg font-bold text-slate-900'>
        <Shield className='h-5 w-5 text-slate-600' />
        Statut de la double authentification
      </h2>
      <div className='space-y-3'>
        <div className='flex items-center gap-3'>
          {status.totpEnabled ? (
            <ShieldCheck className='h-5 w-5 text-emerald-600' />
          ) : (
            <ShieldOff className='h-5 w-5 text-amber-500' />
          )}
          <span className='text-sm font-semibold text-slate-800'>
            {status.totpEnabled ? "TOTP activé — application d'authentification configurée" : 'TOTP non activé'}
          </span>
        </div>
        {status.totpEnabled && (
          <div className='flex items-center gap-2 text-sm text-slate-600'>
            <span className='font-semibold'>{status.backupCodesRemaining}</span>
            <span>
              code{status.backupCodesRemaining !== 1 ? 's' : ''} de secours restant
              {status.backupCodesRemaining !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        <div className='rounded-lg bg-slate-50 p-3 text-xs text-slate-500 space-y-1'>
          <p>
            TTL appareils de confiance configuré :{' '}
            <span className='font-semibold'>
              {status.trustedDeviceTtlDays} jour{status.trustedDeviceTtlDays !== 1 ? 's' : ''}
            </span>
          </p>
          <p>
            TTL effectif :{' '}
            <span className='font-semibold'>
              {status.effectiveTtlDays} jour{status.effectiveTtlDays !== 1 ? 's' : ''}
            </span>{' '}
            (cap rôle : {status.roleCapDays} j)
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Setup TOTP ────────────────────────────────────────────────

type TotpStep = 'idle' | 'setup' | 'verify' | 'done';

interface TotpSetupSectionProps {
  onDone: () => void;
}

function TotpSetupSection({ onDone }: TotpSetupSectionProps) {
  const [step, setStep] = useState<TotpStep>('idle');
  const [qrData, setQrData] = useState<{ otpauthUri: string; qrCodeDataUrl: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSetup() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await setupTotpApi();
      setQrData(data);
      setStep('setup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du setup');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleEnable() {
    if (code.length !== 6) return;
    setIsLoading(true);
    setError(null);
    try {
      const { backupCodes: codes } = await enableTotpApi(code);
      setBackupCodes(codes);
      setStep('done');
      toast.success('TOTP activé avec succès');
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code invalide');
    } finally {
      setIsLoading(false);
    }
  }

  function copyBackupCodes() {
    navigator.clipboard.writeText(backupCodes.join('\n')).catch(() => {});
    toast.success('Codes copiés dans le presse-papiers');
  }

  function downloadBackupCodes() {
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'boulesconnect-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (step === 'idle') {
    return (
      <div className='rounded-2xl border border-amber-200 bg-amber-50 p-6'>
        <h2 className='mb-2 flex items-center gap-2 text-lg font-bold text-amber-800'>
          <Smartphone className='h-5 w-5' />
          Configurer l&apos;application d&apos;authentification
        </h2>
        <p className='mb-4 text-sm text-amber-700'>
          Utilisez une application comme Google Authenticator, Authy ou 1Password pour sécuriser votre compte.
        </p>
        <button
          onClick={handleSetup}
          disabled={isLoading}
          className='flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-amber-700 disabled:opacity-60 cursor-pointer'
        >
          {isLoading && <Loader2 className='h-4 w-4 animate-spin' />}
          Configurer l&apos;authentificateur
        </button>
        {error && <p className='mt-3 text-sm text-red-600'>{error}</p>}
      </div>
    );
  }

  if (step === 'setup' && qrData) {
    return (
      <div className='rounded-2xl border border-slate-200 bg-white p-6'>
        <h2 className='mb-4 text-lg font-bold text-slate-900'>Étape 1 — Scannez le QR code</h2>
        <div className='flex flex-col items-center gap-4 mb-6'>
          <img src={qrData.qrCodeDataUrl} alt='QR code TOTP' className='h-48 w-48 rounded-lg border border-slate-200' />
          <details className='w-full text-center'>
            <summary className='cursor-pointer text-xs text-slate-500 hover:text-slate-700'>
              Afficher la clé secrète (saisie manuelle)
            </summary>
            <p className='mt-2 break-all rounded bg-slate-50 p-2 font-mono text-xs text-slate-700'>{qrData.secret}</p>
          </details>
        </div>

        <h3 className='mb-2 text-sm font-semibold text-slate-800'>Étape 2 — Entrez le code généré</h3>
        <input
          type='text'
          inputMode='numeric'
          maxLength={6}
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder='000000'
          className='mb-4 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-center font-mono text-xl tracking-widest text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500'
        />
        {error && <p className='mb-3 text-sm text-red-600'>{error}</p>}
        <button
          onClick={handleEnable}
          disabled={isLoading || code.length !== 6}
          className='flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60 cursor-pointer'
        >
          {isLoading && <Loader2 className='h-4 w-4 animate-spin' />}
          Activer
        </button>
      </div>
    );
  }

  if (step === 'done' && backupCodes.length > 0) {
    return (
      <div className='rounded-2xl border border-emerald-200 bg-emerald-50 p-6'>
        <h2 className='mb-2 flex items-center gap-2 text-lg font-bold text-emerald-800'>
          <CheckCircle className='h-5 w-5' />
          TOTP activé — Sauvegardez vos codes de secours
        </h2>
        <p className='mb-4 text-sm text-emerald-700'>
          Ces codes ne seront affichés qu&apos;une seule fois. Conservez-les dans un endroit sûr.
        </p>
        <div className='mb-4 grid grid-cols-2 gap-2'>
          {backupCodes.map(c => (
            <code
              key={c}
              className='rounded bg-white px-3 py-1.5 text-center font-mono text-sm font-semibold text-slate-800 shadow-sm'
            >
              {c}
            </code>
          ))}
        </div>
        <div className='flex gap-3'>
          <button
            onClick={copyBackupCodes}
            className='flex flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 cursor-pointer'
          >
            <ClipboardCopy className='h-4 w-4' />
            Copier
          </button>
          <button
            onClick={downloadBackupCodes}
            className='flex flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 cursor-pointer'
          >
            <Download className='h-4 w-4' />
            Télécharger
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// ─── Désactiver TOTP ──────────────────────────────────────────

function DisableTotpSection({ onDisabled }: { onDisabled: () => void }) {
  const [isLoading, setIsLoading] = useState(false);

  async function handleDisable() {
    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir désactiver la double authentification ? Votre compte sera moins sécurisé.',
    );
    if (!confirmed) return;
    setIsLoading(true);
    try {
      await disableTotpApi();
      toast.success('TOTP désactivé');
      onDisabled();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <button
      onClick={handleDisable}
      disabled={isLoading}
      className='mt-4 flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60 cursor-pointer'
    >
      {isLoading ? <Loader2 className='h-4 w-4 animate-spin' /> : <ShieldOff className='h-4 w-4' />}
      Désactiver le TOTP
    </button>
  );
}

// ─── Backup Codes Section ─────────────────────────────────────

function BackupCodesSection({ remaining }: { remaining: number }) {
  const queryClient = useQueryClient();
  const [newCodes, setNewCodes] = useState<string[]>([]);

  const { mutate: regenerate, isPending } = useMutation({
    mutationFn: regenerateBackupCodesApi,
    onSuccess: data => {
      setNewCodes(data.backupCodes);
      toast.success('Codes de secours régénérés');
      void queryClient.invalidateQueries({ queryKey: queryKeys.mfa.status() });
    },
    onError: err => {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    },
  });

  function copyAll() {
    navigator.clipboard.writeText(newCodes.join('\n')).catch(() => {});
    toast.success('Codes copiés');
  }

  function downloadAll() {
    const blob = new Blob([newCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'boulesconnect-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-6'>
      <h2 className='mb-2 flex items-center gap-2 text-lg font-bold text-slate-900'>
        <Shield className='h-5 w-5 text-slate-600' />
        Codes de secours
      </h2>
      <p className='mb-4 text-sm text-slate-500'>
        {remaining} code{remaining !== 1 ? 's' : ''} restant{remaining !== 1 ? 's' : ''}.{' '}
        {remaining < 3 && <span className='text-amber-600 font-semibold'>Régénérez vos codes dès que possible.</span>}
      </p>

      {newCodes.length > 0 && (
        <div className='mb-4'>
          <p className='mb-2 text-xs font-semibold text-slate-700'>Vos nouveaux codes (à sauvegarder) :</p>
          <div className='mb-3 grid grid-cols-2 gap-2'>
            {newCodes.map(c => (
              <code
                key={c}
                className='rounded bg-slate-50 px-3 py-1.5 text-center font-mono text-sm font-semibold text-slate-800'
              >
                {c}
              </code>
            ))}
          </div>
          <div className='flex gap-3'>
            <button
              onClick={copyAll}
              className='flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer'
            >
              <ClipboardCopy className='h-4 w-4' />
              Copier
            </button>
            <button
              onClick={downloadAll}
              className='flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer'
            >
              <Download className='h-4 w-4' />
              Télécharger
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => regenerate()}
        disabled={isPending}
        className='flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-900 disabled:opacity-60 cursor-pointer'
      >
        {isPending ? <Loader2 className='h-4 w-4 animate-spin' /> : <RefreshCw className='h-4 w-4' />}
        Régénérer les codes
      </button>
    </div>
  );
}

// ─── TTL Section ──────────────────────────────────────────────

function TtlSection({ status }: { status: MfaStatus }) {
  const queryClient = useQueryClient();
  const [ttl, setTtl] = useState<number>(() => status.trustedDeviceTtlDays);
  const [ttlError, setTtlError] = useState<string | null>(null);

  const { mutate: saveTtl, isPending } = useMutation({
    mutationFn: (days: number) => updateSecuritySettingsApi({ trustedDeviceTtlDays: days }),
    onSuccess: () => {
      toast.success('Paramètres enregistrés');
      void queryClient.invalidateQueries({ queryKey: queryKeys.mfa.status() });
    },
    onError: err => {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    },
  });

  function handleSave() {
    const parsed = ttlSchema.safeParse({ trustedDeviceTtlDays: ttl });
    if (!parsed.success) {
      setTtlError(parsed.error.issues[0]?.message ?? 'Valeur invalide');
      return;
    }
    setTtlError(null);
    saveTtl(ttl);
  }

  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-6'>
      <h2 className='mb-2 flex items-center gap-2 text-lg font-bold text-slate-900'>
        <Smartphone className='h-5 w-5 text-slate-600' />
        Durée de confiance des appareils
      </h2>
      <p className='mb-4 text-xs text-slate-500'>
        TTL effectif : <span className='font-semibold'>{status.effectiveTtlDays} jours</span> (cap rôle :{' '}
        {status.roleCapDays} jours)
      </p>
      <div className='flex items-center gap-3'>
        <input
          type='number'
          min={1}
          max={90}
          value={ttl}
          pattern='[0-9]*'
          onChange={e => setTtl(parseInt(e.target.value, 10))}
          className='w-24 rounded-xl border border-slate-200 px-3 py-2 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500'
        />
        <span className='text-sm text-slate-600'>jours (1–90)</span>
        <button
          onClick={handleSave}
          disabled={isPending}
          className='ml-auto flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60 cursor-pointer'
        >
          {isPending && <Loader2 className='h-4 w-4 animate-spin' />}
          Enregistrer
        </button>
      </div>
      {ttlError && <p className='mt-2 text-xs text-red-600'>{ttlError}</p>}
    </div>
  );
}

// ─── Trusted Devices Section ───────────────────────────────────

function TrustedDevicesSection({ devices }: { devices: TrustedDevice[] }) {
  const queryClient = useQueryClient();

  const { mutate: revoke, isPending: isRevoking } = useMutation({
    mutationFn: (id: number) => revokeTrustedDeviceApi(id),
    onSuccess: () => {
      toast.success('Appareil révoqué');
      void queryClient.invalidateQueries({ queryKey: queryKeys.mfa.trustedDevices() });
    },
    onError: err => {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    },
  });

  const { mutate: revokeAll, isPending: isRevokingAll } = useMutation({
    mutationFn: revokeAllTrustedDevicesApi,
    onSuccess: () => {
      toast.success('Tous les appareils ont été révoqués');
      void queryClient.invalidateQueries({ queryKey: queryKeys.mfa.trustedDevices() });
    },
    onError: err => {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    },
  });

  function handleRevokeAll() {
    const confirmed = window.confirm('Révoquer tous les appareils de confiance ?');
    if (confirmed) revokeAll();
  }

  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-6'>
      <div className='mb-4 flex items-center justify-between'>
        <h2 className='flex items-center gap-2 text-lg font-bold text-slate-900'>
          <Smartphone className='h-5 w-5 text-slate-600' />
          Appareils de confiance
        </h2>
        {devices.length > 0 && (
          <button
            onClick={handleRevokeAll}
            disabled={isRevokingAll}
            className='flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60 cursor-pointer'
          >
            {isRevokingAll ? <Loader2 className='h-3 w-3 animate-spin' /> : <Trash2 className='h-3 w-3' />}
            Révoquer tous
          </button>
        )}
      </div>

      {devices.length === 0 ? (
        <p className='text-sm text-slate-500'>Aucun appareil de confiance enregistré.</p>
      ) : (
        <div className='space-y-3'>
          {devices.map(d => (
            <div key={d.id} className='flex items-start justify-between gap-3 rounded-xl border border-slate-100 p-3'>
              <div className='flex-1 min-w-0'>
                <p className='truncate text-sm font-semibold text-slate-800'>{d.label ?? 'Appareil inconnu'}</p>
                <p className='text-xs text-slate-500'>Dernière utilisation : {formatDate(d.lastUsedAt)}</p>
                <p className='text-xs text-slate-500'>Expire : {formatDate(d.expiresAt)}</p>
                {d.ipFirstSeen && <p className='text-xs text-slate-400'>IP : {d.ipFirstSeen}</p>}
              </div>
              <button
                onClick={() => revoke(d.id)}
                disabled={isRevoking}
                className='flex-shrink-0 rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-50 cursor-pointer'
                aria-label='Révoquer cet appareil'
              >
                {isRevoking ? <Loader2 className='h-4 w-4 animate-spin' /> : <Trash2 className='h-4 w-4' />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Composant principal ───────────────────────────────────────

export default function SecuritySettingsPanel() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();

  const {
    data: status,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: queryKeys.mfa.status(),
    queryFn: getMfaStatusApi,
    enabled: isAuthenticated,
  });

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: queryKeys.mfa.trustedDevices(),
    queryFn: getTrustedDevicesApi,
    enabled: isAuthenticated,
  });

  const isLoading = statusLoading || devicesLoading || !isAuthenticated;

  function handleTotpChange() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.mfa.status() });
    void refetchStatus();
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Loader2 className='h-8 w-8 animate-spin text-slate-400' />
      </div>
    );
  }

  if (!status) {
    return (
      <div className='flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700'>
        <AlertTriangle className='h-5 w-5 shrink-0' />
        Impossible de charger les informations de sécurité.
      </div>
    );
  }

  return (
    <div className='space-y-6 max-w-2xl'>
      <MfaStatusSection status={status} />

      {!status.totpEnabled && <TotpSetupSection onDone={handleTotpChange} />}

      {status.totpEnabled && (
        <>
          <BackupCodesSection remaining={status.backupCodesRemaining} />
          <DisableTotpSection onDisabled={handleTotpChange} />
        </>
      )}

      <TtlSection status={status} />

      <TrustedDevicesSection devices={devices ?? []} />
    </div>
  );
}
